import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../../AppContext';
import Status from '../../../core/types/Status';
import styled, { css } from 'styled-components';
import ProvidersModal from './ProvidersModal';
import CasesModal from './CasesModal';
import ApiProvider from '../../../core/api/ApiProvider';
// @ts-ignore
import styles from './PayAllCustomerTransactionsTile.module.scss';
import { PayableItem } from './types/PayableItem';
import { PaymentProviderType } from '../../types/PaymentProviderType';
import withAbortRequest, { AbortRequestPropsType } from '../../../core/hoc/withAbortRequest';
import { InfoMessageService, StyleService, TranslationService } from '../../../core/services';
import { Button, Loader, ModuleContainer } from '../../../core/components';
import { RedirectContentResponse } from '../../types/RedirectContentResponse';
import { DictionaryType } from '../../../core/types/DictionaryType';

type ModelType = {
  isPaymentsFuncTurnOn: boolean;
  displayBalanceAmount: boolean;
  allCasesBalance: string;
  currency: string;
  items: PayableItem[];
  paymentProviderTypes: PaymentProviderType[];
};

const PayAllCustomerTransactionsTileModule = (props: AbortRequestPropsType) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [model, setModel] = useState<ModelType>({
    allCasesBalance: '0',
    currency: '',
  } as ModelType);
  const [isOpenProvidersModal, setIsOpenProvidersModal] = useState<boolean>(false);
  const [isOpenCasesModal, setIsOpenCasesModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<PayableItem>();

  const context = useAppContext();
  const whitelabelingStyles = StyleService.applyMany(
    ['module:paynow:firstColor', 'module:paynow:secondColor', 'module:paynow:payButton'],
    context.styles
  );

  const getPaymentUrl = async (selectedItem: PayableItem, providerType: PaymentProviderType) => {
    return await ApiProvider.post<Status<string>>(
      'PayAllCustomerTransactionsTileModule/GetARMPaymentServiceUrl',
      {
        accountNo: selectedItem.accountNo,
        creditorNo: selectedItem.creditorNo,
        invoiceNo: selectedItem.invoiceNo,
        debtorNo: selectedItem.debtorNo,
        providerType: providerType,
      },
      props.cancelTokenSource.token
    );
  };

  const fetchModel = async () => {
    try {
      setIsLoading(true);
      const data = await ApiProvider.get<Status<ModelType>>(
        'PayAllCustomerTransactionsTileModule/GetPayAllTile',
        props.cancelTokenSource.token
      );
      setModel(data.data);
    } catch (err) {
      InfoMessageService.error(TranslationService.translate('ErrorSthWentWrong'));
    } finally {
      setIsLoading(false);
    }
  };

  const goToPaymentsPage = async (selectedItem: PayableItem, provider: PaymentProviderType) => {
    var response = await getPaymentUrl(selectedItem, provider);
    if (response.success) {
      var redirectReponse = await ApiProvider.get<Status<RedirectContentResponse>>(response.data);
      if (redirectReponse.data.method === 'GET') {
        window.location.href = redirectReponse.data.url;
      } else {
        const r = document.createRange();
        const f = r.createContextualFragment(redirectReponse.data.body);
        document.getElementsByTagName('body')[0].appendChild(f);
        document.querySelector<HTMLButtonElement>('#submit-button')!.click();
      }
    } else {
      InfoMessageService.error(response.message);
    }
    setIsOpenCasesModal(false);
    setIsOpenProvidersModal(false);
  };

  const onPayAllTileClick = (e: any) => {
    if (isLoading) {
      return;
    }
    if (e.type === 'click' || (e.type === 'keydown' && e.keyCode === 13)) {
      if (model.items.length > 1) {
        setIsOpenCasesModal(true);
      } else if (model.items.length === 1) {
        if (model.paymentProviderTypes.length > 1) {
          setSelectedItem(model.items[0]);
          setIsOpenProvidersModal(true);
        } else {
          goToPaymentsPage(model.items[0], model.paymentProviderTypes[0]);
        }
      }
    }
  };

  const payArmInvoice = (payInvoice: PayableItem) => {
    setSelectedItem(payInvoice);
    setIsOpenCasesModal(false);
    setIsOpenProvidersModal(true);
  };

  const onPay = (providerType: PaymentProviderType) => {
    if (!selectedItem) return;
    goToPaymentsPage(selectedItem, providerType);
  };

  // @ts-ignore
  useEffect(() => {
    let isSubscribed = true;
    (async () => {
      await fetchModel();

      if (isSubscribed) {
        setIsLoading(false);
      }
    })();

    return () => (isSubscribed = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ModuleContainer variant={ModuleContainer.ModuleVariant.Secondary}>
      <PayAllTileDiv
        id="PayAllCustomerTransactionsWrapper"
        className={`${styles.wrapper}`}
        styles={whitelabelingStyles}
      >
        {isLoading && <Loader opacity={0.4} />}

        {model.displayBalanceAmount && (
          <div className={styles.amount}>
            {model.allCasesBalance}
            <em className={styles.currencySymbol}>{model.currency}</em>
          </div>
        )}

        <div className={styles.buttonsWrapper}>
          <Button
            id="PayNowButton"
            type={Button.Types.Button}
            variant={Button.Variants.Primary}
            classNames={styles.button}
            onClick={(e) => (model.isPaymentsFuncTurnOn ? onPayAllTileClick(e) : undefined)}
          >
            {model.isPaymentsFuncTurnOn
              ? TranslationService.translate('PayNow')
              : TranslationService.translate('TotalBalance')}
          </Button>
        </div>
      </PayAllTileDiv>

      {selectedItem && (
        <ProvidersModal
          isOpen={isOpenProvidersModal}
          onToggle={setIsOpenProvidersModal}
          selectedItem={selectedItem}
          onPay={onPay}
        />
      )}

      <CasesModal
        isOpen={isOpenCasesModal}
        onToggle={setIsOpenCasesModal}
        onPayInvoice={payArmInvoice}
      />
    </ModuleContainer>
  );
};

const PayAllTileDiv = styled.div<{
  styles: DictionaryType<DictionaryType<string>>;
}>`
  ${(props) => {
    let styles = '';

    if (props.styles['module:paynow:firstColor'].hasOwnProperty('backgroundColor')) {
      styles = `${styles} --module-paynow-tile-color-first: ${props.styles['module:paynow:firstColor'].backgroundColor};`;
    }

    if (props.styles['module:paynow:secondColor'].hasOwnProperty('backgroundColor')) {
      styles = `${styles} --module-paynow-tile-color-second: ${props.styles['module:paynow:secondColor'].backgroundColor};`;
    }

    if (props.styles['module:paynow:payButton'].hasOwnProperty('textColor')) {
      styles = `${styles} --module-paynow-tile-payButton-color: ${props.styles['module:paynow:payButton'].textColor};`;
    }

    if (props.styles['module:paynow:payButton'].hasOwnProperty('backgroundColor')) {
      styles = `${styles} --module-paynow-tile-payButton-bacground-color: ${props.styles['module:paynow:payButton'].backgroundColor};`;
    }

    if (props.styles['module:paynow:payButton'].hasOwnProperty('textColorHover')) {
      styles = `${styles} --module-paynow-tile-payButton-color-hover: ${props.styles['module:paynow:payButton'].textColorHover};`;
    }

    if (props.styles['module:paynow:payButton'].hasOwnProperty('backgroundColorHover')) {
      styles = `${styles} --module-paynow-tile-payButton-bacground-color-hover: ${props.styles['module:paynow:payButton'].backgroundColorHover};`;
    }

    return css`
      ${styles}
    `;
  }}
`;

export default withAbortRequest(PayAllCustomerTransactionsTileModule);
